/**
 * CV Creator Service
 * API calls for creating tailored CVs
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface CreateTailoredCVRequest {
    cv_id: number;  // 0 if uploading a new CV file
    role_description?: string;
    cv_file?: File;  // CV file if uploading instead of selecting from database
}

export interface CreateTailoredCVResult {
    success: boolean;
    conversionId: string;
    convertedFilename: string;
    downloadUrl: string;
    associateName?: string;
    temporary?: boolean;
    message?: string;
}

export interface ProgressUpdate {
    progress: number;
    message: string;
}

/**
 * Create a tailored CV based on role description
 */
export async function createTailoredCV(
    request: CreateTailoredCVRequest,
    onProgress?: (update: ProgressUpdate) => void
): Promise<CreateTailoredCVResult> {
    try {
        const formData = new FormData();
        formData.append('cv_id', request.cv_id.toString());
        
        if (request.role_description) {
            formData.append('role_description', request.role_description);
        }
        
        if (request.cv_file) {
            formData.append('cv_file', request.cv_file);
        }

        const isUploadingCV = request.cv_file !== undefined;
        
        if (onProgress) {
            onProgress({ 
                progress: 30, 
                message: isUploadingCV ? 'Processing uploaded CV...' : 'Fetching CV from database...' 
            });
        }

        const response = await axios.post<CreateTailoredCVResult>(
            `${API_URL}/CVCreatorFunction`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                withCredentials: true,
                timeout: 180000, // 3 minutes - CV generation can take time
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 20) / progressEvent.total);
                        onProgress({ 
                            progress: 10 + percentCompleted, 
                            message: 'Uploading role description...' 
                        });
                    }
                }
            }
        );

        if (onProgress) {
            onProgress({ progress: 95, message: 'Finalizing CV...' });
        }

        return response.data;

    } catch (error: any) {
        console.error('Create tailored CV failed:', error);

        if (axios.isAxiosError(error)) {
            if (error.code === 'ERR_NETWORK') {
                throw new Error('Service unavailable. Please try again later.');
            }
            if (error.code === 'ECONNABORTED') {
                throw new Error('Request timed out. The CV may be too large or complex.');
            }
            if (error.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            if (error.response?.status === 404) {
                throw new Error('CV not found in database.');
            }
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            if (error.response?.data?.details) {
                throw new Error(error.response.data.details);
            }
        }

        throw new Error('Failed to create tailored CV. Please try again.');
    }
}

